import { RequestFailure } from "../components/RequestFailure";
import { t as msg, countText, formatNumber } from "../lib/i18n";
import { useEffect, useId, useState, useRef } from "react";
import {
  UsersRound,
  ArrowRight,
  ArrowLeft,
  Send,
  Search,
  Settings2,
  ChevronDown,
} from "lucide-react";
import type {
  Friend,
  Person,
  User,
  Card,
} from "../../../../packages/shared/src/types";
import { api, cardStatusText } from "../lib/api";
import { CardPreview } from "../components/CardPreview";
import { ConfirmDialog } from "../components/ConfirmDialog";

function Avatar({ person }: { person: Friend }) {
  return (
    <span className="friend-avatar" aria-hidden="true">
      {Array.from(person.note || person.displayName)[0]?.toUpperCase()}
    </span>
  );
}
function FriendEntry({ friend }: { friend: Friend }) {
  return (
    <li className="friend-entry">
      <a className="friend-person" href={`/friends/${friend.id}`}>
        <Avatar person={friend} />
        <span className="friend-name">
          <strong>{friend.note || friend.displayName}</strong>
          {friend.note && <small>{friend.displayName}</small>}
          <span className="friend-shared">
            {msg("Shared cards")}<ArrowRight size={14} aria-hidden="true" />
          </span>
        </span>
        {friend.isNew && <span className="friend-new">{msg("New")}</span>}
      </a>
      <a
        className="button secondary friend-send"
        href={`/create?friend=${friend.id}`}
        aria-label={msg("Send a card to {p0}", {p0: friend.note || friend.displayName})}
      >
        <Send size={16} aria-hidden="true" /> {msg("Send card")}</a>
    </li>
  );
}
export function Friends({
  user,
  signIn,
}: {
  user: User | null;
  signIn: () => void;
}) {
  const peerId = location.pathname.split("/")[2];
  const searchId = useId();
  const [friends, setFriends] = useState<Friend[]>([]),
    [blocks, setBlocks] = useState<Person[]>([]),
    [cards, setCards] = useState<Card[]>([]),
    [friend, setFriend] = useState<Friend | null>(null),
    [note, setNote] = useState(""),
    [query, setQuery] = useState(""),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [loadProblem, setLoadProblem] = useState<Error | null>(null),
    [confirm, setConfirm] = useState<"remove" | "block" | null>(null),
    [saved, setSaved] = useState(false),
    [nextCursor, setNextCursor] = useState<string | null>(null);
  const requestVersion = useRef(0), writing = useRef(false), noteEdited = useRef(false);
  async function read() {
    if (peerId) {
      const r = await api<{
        friend: Friend;
        cards: Card[];
        nextCursor: string | null;
      }>(`/api/friends/${peerId}`);
      return { friend: r.friend, cards: r.cards, nextCursor: r.nextCursor };
    }
    const [f, b] = await Promise.all([
      api<{ friends: Friend[] }>("/api/friends"),
      api<{ blocks: Person[] }>("/api/blocks"),
    ]);
    return { friends: f.friends, blocks: b.blocks };
  }
  function apply(r: Awaited<ReturnType<typeof read>>) {
    if (r.friend) {
      setFriend(r.friend);
      if (!noteEdited.current) setNote(r.friend.note);
      setCards(r.cards!);
      setNextCursor(r.nextCursor || null);
    } else {
      setFriends(r.friends!);
      setBlocks(r.blocks!);
    }
  }
  async function refresh() {
    const version = ++requestVersion.current;
    setLoadProblem(null);
    try { const r = await read(); if (version === requestVersion.current) apply(r); }
    catch (e) { if (version === requestVersion.current) setLoadProblem(e as Error); }
    finally { if (version === requestVersion.current) setLoading(false); }
  }
  useEffect(() => {
    if (user) void refresh(); else setLoading(false);
    return () => { requestVersion.current++; };
  }, [user?.id, peerId]);
  async function act(
    kind: "note" | "remove" | "block" | "unblock",
    id = peerId,
  ) {
    if (writing.current) return;
    writing.current = true; setBusy(true);
    const version = ++requestVersion.current, value = note.trim();
    setError("");
    try {
      if (kind === "note") {
        await api(`/api/friends/${id}/note`, "PATCH", { note: value });
        if (version !== requestVersion.current) return;
        setFriend((previous) => (previous ? { ...previous, note: value } : previous));
        setNote(value); noteEdited.current = false; setSaved(true);
      }
      if (kind === "remove") await api(`/api/friends/${id}`, "DELETE");
      if (kind === "block") await api("/api/blocks", "POST", { userId: id });
      if (kind === "unblock") await api(`/api/blocks/${id}`, "DELETE");
      if (version !== requestVersion.current) return;
      if (kind === "remove" || kind === "block") location.assign("/friends");
      else if (kind === "unblock") setBlocks((previous) => previous.filter((person) => person.id !== id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      writing.current = false; setBusy(false);
    }
  }
  const unseen = friend
    ? friend.isNew
      ? [friend]
      : []
    : friends.filter((f) => f.isNew);
  const matches = friends.filter((f) =>
    `${f.displayName} ${f.note}`
      .toLocaleLowerCase()
      .includes(query.trim().toLocaleLowerCase()),
  );
  async function dismissNotice() {
    if (busy) return;
    setBusy(true);
    try {
      for (const person of unseen)
        await api(`/api/friends/${person.id}/seen`, "POST", {});
      setFriend((previous) => previous ? { ...previous, isNew: false } : previous);
      setFriends((previous) => previous.map((person) => ({ ...person, isNew: false })));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function loadMore() {
    if (busy || !nextCursor) return;
    setBusy(true);
    try {
      const r = await api<{ cards: Card[]; nextCursor: string | null }>(
        `/api/friends/${peerId}?cursor=${encodeURIComponent(nextCursor!)}`,
      );
      setCards((previous) => [
        ...new Map([...previous, ...r.cards].map((c) => [c.id, c])).values(),
      ]);
      setNextCursor(r.nextCursor);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  if (!user)
    return (
      <section className="wrap section narrow friends-page">
        <p className="eyebrow">{msg("YOUR PEOPLE, ONE TAP AWAY")}</p>
        <h1 className="page-title">{msg("Good people, close by.")}</h1>
        <p>
          {msg("Sign in to find the people you’ve exchanged promises with. Send cards straight to each other’s accounts, with no extra friend request.")}</p>
        <button className="button" onClick={signIn}>
          {msg("Sign in to Friends")}<ArrowRight size={17} aria-hidden="true" />
        </button>
      </section>
    );
  return (
    <section className="wrap section friends-page">
      {peerId && (
        <a className="text-link" href="/friends">
          <ArrowLeft size={16} aria-hidden="true" /> {msg("All friends")}</a>
      )}
      <div className="section-heading friends-heading">
        <div className={friend ? "friend-identity" : undefined}>
          {friend && <Avatar person={friend} />}
          <div>
            <h1 className="page-title">
              {friend
                ? friend.note || friend.displayName
                : peerId
                  ? msg("Your connection.")
                  : msg("Friends.")}
            </h1>
          </div>
        </div>
        {friend ? (
          <div className="friend-actions">
            <a className="button" href={`/create?friend=${friend.id}`}>
              <Send size={17} aria-hidden="true" /> {msg("Send card")}</a>
          </div>
        ) : (
          !peerId &&
          !!friends.length && (
            <a className="button secondary" href="/create">
              <Send size={17} aria-hidden="true" /> {msg("Send to someone new")}</a>
          )
        )}
      </div>
      {loadProblem && <RequestFailure error={loadProblem} retry={() => void refresh()} signIn={signIn} />}
      {error && <p className="error" role="alert">{error}</p>}
      {!!unseen.length && (
        <aside className="friend-notice" aria-label={msg("New friendships")}>
          <UsersRound size={24} aria-hidden="true" />
          <div>
            <strong>
              {friend ? msg("You’re now friends.") : msg("Your circle just grew.")}
            </strong>
            <p>
              {msg("You can now send cards directly to {names}.",{names:unseen.slice(0,5).map(f=>f.note || f.displayName).join(", ")})}</p>
          </div>
          <button
            className="text-button"
            disabled={busy}
            onClick={() => void dismissNotice()}
          >
            {msg("Got it")}</button>
        </aside>
      )}
      {loading ? (
        <p role="status">{msg("Gathering your connections…")}</p>
      ) : friend ? (
        <>
          <details
            className="friend-settings"
          >
            <summary>
              <Settings2 size={18} aria-hidden="true" />
              <span>{msg("Manage this friendship")}</span>
              <ChevronDown size={18} aria-hidden="true" />
            </summary>
            <div className="friend-details">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void act("note");
                }}
              >
                <label>
                  {msg("Your private name for them")}<input
                    maxLength={60}
                    disabled={busy}
                    value={note}
                    onChange={(e) => {
                      setNote(e.target.value);
                      noteEdited.current = true; setSaved(false);
                    }}
                    placeholder={friend.displayName}
                  />
                </label>
                <p className="muted">
                  {msg("Only you can see this. Their profile name stays unchanged.")}</p>
                <button
                  className="button secondary"
                  disabled={busy || note === friend.note}
                >
                  {msg("Save note")}</button>
                {saved && (
                  <span role="status">{msg("Saved. Only you can see this.")}</span>
                )}
              </form>
              <div className="friend-safety">
                <p>
                  {msg("Existing cards stay in My cards if you remove a friend. Blocking also stops new interactions.")}</p>
                <div className="inline-links">
                  <button
                    className="text-button"
                    onClick={() => setConfirm("remove")}
                  >
                    {msg("Remove friend")}</button>
                  <button
                    className="text-button"
                    onClick={() => setConfirm("block")}
                  >
                    {msg("Block account")}</button>
                </div>
              </div>
            </div>
          </details>
          <div className="shared-heading">
            <h2>{msg("Your shared cards.")}</h2>
            <p>{msg("From you. From them. All in one place.")}</p>
          </div>
          <div className="collection-grid friend-cards">
            {cards.map((c) => (
              <article className="collection-item" key={c.id}>
                <a href={`/cards/${c.id}`} aria-label={msg("Open {p0}", {p0: c.title})}><CardPreview card={c} /></a>
                <div className="item-body">
                  <p className="eyebrow">
                    {c.sender?.id === user.id ? msg("YOU SENT") : msg("THEY SENT")}
                  </p>
                  <a className="text-link" href={`/cards/${c.id}`}>{msg("Open card")}<ArrowRight size={15} aria-hidden="true" /></a>
                </div>
              </article>
            ))}
          </div>
          {!cards.length && (
            <p className="muted">
              {msg("No available cards to show. Make a new promise to start your next exchange.")}</p>
          )}
          {nextCursor && (
            <button
              className="button secondary"
              disabled={busy}
              onClick={() => void loadMore()}
            >
              {busy ? msg("Loading…") : msg("Load more cards")}
            </button>
          )}

        </>
      ) : !peerId ? (
        <>
          <div className="friends-toolbar">
            <label className="friend-search" htmlFor={searchId}>
              {msg("Find a friend")}<span className="friend-search-field">
                <Search size={19} aria-hidden="true" />
                <input
                  id={searchId}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={msg("Name or private note")}
                />
              </span>
            </label>
            <p role="status" className="friend-result-count">
              {query ? msg("{p0} of {p1}",{p0:formatNumber(matches.length),p1:countText("friendCount",friends.length)}) : countText("friendCount",friends.length)}
            </p>
          </div>
          {!friends.length && !error && (
            <div className="empty friend-empty">
              <UsersRound size={36} strokeWidth={1.3} aria-hidden="true" />
              <h2>{msg("A small circle starts with a promise.")}</h2>
              <p>
                {msg("Send a link to someone you know. Accepting or fulfilling a promise between your accounts makes you friends automatically. You can then send directly, even without email.")}</p>
              <a className="button" href="/create">
                {msg("Make your first connection")}{" "}
                <ArrowRight size={17} aria-hidden="true" />
              </a>
            </div>
          )}
          <ul className="friend-list">
            {matches.map((f) => (
              <FriendEntry key={f.id} friend={f} />
            ))}
          </ul>
          {!!friends.length && !matches.length && (
            <div className="friend-no-results">
              <h2>{msg("No friends found.")}</h2>
              <p>{msg("Try their profile name or the private note you saved.")}</p>
              <button className="button secondary" onClick={() => setQuery("")}>
                {msg("Clear search")}</button>
            </div>
          )}
          {!!blocks.length && (
            <details className="blocked-list friend-settings">
              <summary>
                <Settings2 size={18} aria-hidden="true" />
                <span>{msg("Blocked accounts (")}{blocks.length})</span>
                <ChevronDown size={18} aria-hidden="true" />
              </summary>
              <p>
                {msg("Unblocking allows future exchanges. It does not restore a friendship.")}</p>
              {blocks.map((p) => (
                <div className="blocked-row" key={p.id}>
                  <strong>{p.displayName}</strong>
                  <button
                    className="text-button"
                    disabled={busy}
                    onClick={() => void act("unblock", p.id)}
                  >
                    {msg("Unblock")}</button>
                </div>
              ))}
            </details>
          )}
        </>
      ) : null}
      {confirm && (
        <ConfirmDialog
          title={
            confirm === "remove"
              ? msg("Remove this friendship?")
              : msg("Block this account?")
          }
          busy={busy}
          confirmLabel={
            confirm === "remove" ? msg("Remove friend") : msg("Block account")
          }
          cancelLabel={msg("Cancel")}
          onConfirm={() => void act(confirm)}
          onCancel={() => setConfirm(null)}
        >
          {confirm === "remove"
            ? msg("This removes the friendship for both of you. Existing cards remain; only a new link card with a positive exchange can reconnect you.")
            : msg("This removes the friendship and stops new cards, requests, thanks and reminders between you. Existing requests can still be withdrawn or completed.")}
        </ConfirmDialog>
      )}
    </section>
  );
}
